import { parseAksharas } from "../parser";
import type { Lesson } from "../types";

/**
 * The 22-lesson curriculum, in teaching order.
 *
 * Units:
 *   1 — vowels            (l01–l03)
 *   2 — consonants        (l04–l11)
 *   3 — matras            (l12–l15)
 *   4 — numerals          (l16)
 *   5 — halant/conjuncts  (l17–l20)
 *   6 — nukta letters     (l21)
 *   7 — fluency/sentences (l22)
 *
 * Every `wordId`/`sentenceId` a lesson references is spellable using only the
 * items taught up to that lesson — enforced mechanically by
 * `content-lint.test.ts` via {@link isReadable}. Do not add a word to a lesson
 * before every one of its aksharas is teachable; the test will fail loudly.
 */
export const lessonList: Lesson[] = [
  // ── Unit 1: vowels ──────────────────────────────────────────────────────
  {
    id: "l01-vowels-1",
    unit: 1,
    titleHi: "स्वर — १",
    titleEn: "Vowels 1",
    newItemIds: ["vowel-a", "vowel-aa", "vowel-i", "vowel-ii"],
    wordIds: [],
  },
  {
    id: "l02-vowels-2",
    unit: 1,
    titleHi: "स्वर — २",
    titleEn: "Vowels 2",
    newItemIds: ["vowel-u", "vowel-uu", "vowel-e", "vowel-ai"],
    wordIds: [],
  },
  {
    id: "l03-vowels-3",
    unit: 1,
    titleHi: "स्वर — ३",
    titleEn: "Vowels 3 & nasal/aspirate signs",
    newItemIds: [
      "vowel-o",
      "vowel-au",
      "vowel-ri",
      "sign-anusvara",
      "sign-visarga",
    ],
    wordIds: [],
  },
  // ── Unit 2: consonants (inherent-a words only — no matras yet) ───────────
  {
    id: "l04-ka-varga",
    unit: 2,
    titleHi: "क वर्ग",
    titleEn: "Ka-varga (velars)",
    newItemIds: ["ka", "kha", "ga", "gha", "nga"],
    wordIds: ["w-ek", "w-aag", "w-ang", "w-ank", "w-ug", "w-khag"],
  },
  {
    id: "l05-ca-varga",
    unit: 2,
    titleHi: "च वर्ग",
    titleEn: "Ca-varga (palatals)",
    newItemIds: ["ca", "cha", "ja", "jha", "nya"],
    wordIds: ["w-jag", "w-gaj", "w-aaj", "w-jaj", "w-gajak", "w-cakh"],
  },
  {
    id: "l06-tta-varga",
    unit: 2,
    titleHi: "ट वर्ग",
    titleEn: "Ta-varga (retroflex)",
    newItemIds: ["tta", "ttha", "dda", "ddha", "nna"],
    wordIds: ["w-ghat", "w-thag", "w-kan", "w-dank", "w-atak", "w-jhatak"],
  },
  {
    id: "l07-ta-varga",
    unit: 2,
    titleHi: "त वर्ग",
    titleEn: "Ta-varga (dentals)",
    newItemIds: ["ta", "tha", "da", "dha", "na"],
    wordIds: [
      "w-tan",
      "w-dhan",
      "w-jan",
      "w-ghan",
      "w-gagan",
      "w-nath",
      "w-oon",
      "w-ainak",
    ],
  },
  {
    id: "l08-pa-varga",
    unit: 2,
    titleHi: "प वर्ग",
    titleEn: "Pa-varga (labials)",
    newItemIds: ["pa", "pha", "ba", "bha", "ma"],
    wordIds: [
      "w-man",
      "w-namak",
      "w-naman",
      "w-bhajan",
      "w-chamak",
      "w-path",
      "w-aam",
    ],
  },
  {
    id: "l09-antastha",
    unit: 2,
    titleHi: "अन्तःस्थ",
    titleEn: "Semivowels (ya, ra, la, va)",
    newItemIds: ["ya", "ra", "la", "va"],
    wordIds: [
      "w-ghar",
      "w-jal",
      "w-phal",
      "w-kamal",
      "w-garam",
      "w-magar",
      "w-matar",
      "w-bandar",
      "w-aurat",
    ],
  },
  {
    id: "l10-ushma",
    unit: 2,
    titleHi: "ऊष्म",
    titleEn: "Sibilants & ha",
    newItemIds: ["sha", "ssa", "sa", "ha"],
    wordIds: [
      "w-hum",
      "w-sab",
      "w-bas",
      "w-bahan",
      "w-shahar",
      "w-mahal",
      "w-das",
      "w-jagah",
      "w-os",
    ],
  },
  {
    id: "l11-review-consonants",
    unit: 2,
    titleHi: "व्यंजन — दोहराव",
    titleEn: "Consonant review",
    newItemIds: [],
    wordIds: ["w-kal", "w-hal", "w-naram", "w-baras", "w-lahar", "w-bartan"],
  },
  // ── Unit 3: matras ──────────────────────────────────────────────────────
  {
    id: "l12-matras-1",
    unit: 3,
    titleHi: "मात्रा — १",
    titleEn: "Matras 1 (aa, i, ii)",
    newItemIds: ["matra-aa", "matra-i", "matra-ii"],
    wordIds: [
      "w-naam",
      "w-kaam",
      "w-paani",
      "w-din",
      "w-raat",
      "w-baat",
      "w-haath",
      "w-maata",
      "w-pita",
      "w-dadi",
      "w-nani",
      "w-saal",
      "w-chacha",
      "w-bhai",
      "w-shaam",
      "w-naak",
      "w-kaan",
      "w-daal",
      "w-jana",
      "w-aana",
      "w-hawa",
      "w-kamra",
      "w-imli",
      "w-rishi",
    ],
  },
  {
    id: "l13-matras-2",
    unit: 3,
    titleHi: "मात्रा — २",
    titleEn: "Matras 2 (u, uu, e, ai)",
    newItemIds: ["matra-u", "matra-uu", "matra-e", "matra-ai"],
    wordIds: [
      "w-phul",
      "w-guru",
      "w-doodh",
      "w-mez",
      "w-desh",
      "w-paisa",
      "w-subah",
      "w-bahut",
      "w-beta",
      "w-beti",
      "w-tel",
      "w-pair",
      "w-seb",
      "w-aalu",
      "w-dena",
      "w-lena",
      "w-sundar",
    ],
  },
  {
    id: "l14-matras-3",
    unit: 3,
    titleHi: "मात्रा — ३",
    titleEn: "Matras 3 (o, au, ri) & chandrabindu",
    newItemIds: ["matra-o", "matra-au", "matra-ri", "sign-chandrabindu"],
    wordIds: [
      "w-log",
      "w-mor",
      "w-kaun",
      "w-mausam",
      "w-chand",
      "w-gaon",
      "w-maa",
      "w-daant",
      "w-paanch",
      "w-kripa",
      "w-sona",
      "w-aankh",
    ],
  },
  {
    id: "l15-barakhadi",
    unit: 3,
    titleHi: "बारहखड़ी",
    titleEn: "Barakhadi (matra practice)",
    newItemIds: [],
    wordIds: [
      "w-kitab",
      "w-roti",
      "w-khana",
      "w-kahani",
      "w-mahina",
      "w-bagicha",
      "w-sapna",
      "w-duniya",
      "w-karna",
      "w-pina",
      "w-mandir",
      "w-gaajar",
    ],
  },
  // ── Unit 4: numerals ────────────────────────────────────────────────────
  {
    id: "l16-numerals",
    unit: 4,
    titleHi: "अंक",
    titleEn: "Numerals 0–9",
    newItemIds: [
      "num-0",
      "num-1",
      "num-2",
      "num-3",
      "num-4",
      "num-5",
      "num-6",
      "num-7",
      "num-8",
      "num-9",
    ],
    wordIds: [
      "w-ek",
      "w-do",
      "w-teen",
      "w-char",
      "w-paanch",
      "w-chhah",
      "w-saat",
      "w-aath",
      "w-das",
    ],
  },
  // ── Unit 5: halant, conjuncts, ra-forms, gemination ─────────────────────
  {
    id: "l17-halant",
    unit: 5,
    titleHi: "हलन्त",
    titleEn: "Halant (joining consonants)",
    newItemIds: ["sign-halant"],
    wordIds: [
      "w-namaste",
      "w-hindi",
      "w-dost",
      "w-hasta",
      "w-vakt",
      "w-shukriya",
      "w-satya",
      "w-bhakt",
      "w-shakti",
      "w-ank-conj",
    ],
  },
  {
    id: "l18-conjuncts-common",
    unit: 5,
    titleHi: "संयुक्ताक्षर",
    titleEn: "Common conjuncts",
    newItemIds: ["conj-ksa", "conj-tra", "conj-jna", "conj-shra"],
    wordIds: [
      "w-kshama",
      "w-chhatra",
      "w-gyan",
      "w-shram",
      "w-mitra",
      "w-rakshak",
    ],
  },
  {
    id: "l19-ra-forms",
    unit: 5,
    titleHi: "र के रूप",
    titleEn: "Forms of ra",
    newItemIds: [],
    wordIds: [
      "w-pyar",
      "w-prem",
      "w-karm",
      "w-dharm",
      "w-varsha",
      "w-parv",
      "w-surya",
      "w-garv",
    ],
  },
  {
    id: "l20-gemination",
    unit: 5,
    titleHi: "द्वित्व",
    titleEn: "Gemination (doubled consonants)",
    newItemIds: [],
    wordIds: [
      "w-achchha",
      "w-billi",
      "w-rassi",
      "w-gadda",
      "w-bachcha",
      "w-kutta",
      "w-pakka",
      "w-sachcha",
      "w-gussa",
      "w-dibba",
    ],
  },
  // ── Unit 6: nukta ───────────────────────────────────────────────────────
  {
    id: "l21-nukta",
    unit: 6,
    titleHi: "नुक़्ता",
    titleEn: "Nukta letters",
    newItemIds: ["za", "fa", "rra", "rrha", "qa", "khha", "ghha"],
    wordIds: [
      "w-sadak",
      "w-bada",
      "w-padhna",
      "w-ladka",
      "w-zindagi",
      "w-bazaar",
      "w-maza",
      "w-safar",
      "w-tez",
      "w-namaz",
      "w-phone",
      "w-daftar",
      "w-qila",
      "w-khabar",
      "w-gham",
    ],
  },
  // ── Unit 7: fluency ─────────────────────────────────────────────────────
  {
    id: "l22-fluency",
    unit: 7,
    titleHi: "प्रवाह",
    titleEn: "Fluency (sentences)",
    newItemIds: [],
    wordIds: [],
    sentenceIds: [
      "s-namaste",
      "s-how-are-you",
      "s-my-name",
      "s-where-from",
      "s-thank-you",
      "s-water-please",
      "s-i-am-hungry",
      "s-good-morning",
      "s-see-you",
      "s-this-is-my-house",
      "s-food-is-ready",
      "s-what-time",
      "s-i-love-you",
      "s-come-home",
      "s-she-is-my-sister",
      "s-lets-go",
      "s-it-is-hot",
      "s-child-is-sleeping",
      "s-book-on-table",
      "s-how-much",
      "s-i-dont-know",
      "s-please-sit",
      "s-tea-or-milk",
      "s-take-care",
      "s-i-am-fine",
      "s-what-is-your-name",
      "s-i-am-from-india",
      "s-lets-eat",
      "s-the-water-is-cold",
      "s-i-am-going-home",
      "s-good-night",
      "s-please-come-in",
    ],
  },
];

/** Every lesson indexed by its id. */
export const lessonById: ReadonlyMap<string, Lesson> = new Map(
  lessonList.map((lesson) => [lesson.id, lesson]),
);

/**
 * The set of all item ids taught up to and including `uptoLessonId` (the union
 * of every lesson's `newItemIds` from the first lesson through the named one).
 *
 * Returns an empty set for an unknown lesson id — never "everything", so a typo
 * fails closed rather than silently marking all content readable.
 */
export function taughtItemIds(uptoLessonId: string): Set<string> {
  const taught = new Set<string>();
  let found = false;
  for (const lesson of lessonList) {
    for (const itemId of lesson.newItemIds) {
      taught.add(itemId);
    }
    if (lesson.id === uptoLessonId) {
      found = true;
      break;
    }
  }
  return found ? taught : new Set<string>();
}

/**
 * True when a codepoint lies in the Devanagari Unicode block (U+0900–U+097F),
 * excluding the danda (U+0964) and double danda (U+0965) — sentence
 * punctuation that carries no teachable letter of its own.
 */
function isTeachableDevanagari(codePoint: number): boolean {
  if (codePoint === 0x0964 || codePoint === 0x0965) {
    return false; // danda / double danda — punctuation, always acceptable
  }
  return codePoint >= 0x0900 && codePoint <= 0x097f;
}

/**
 * True when every akshara of `devanagari` is spellable using only items in
 * `taught`. A unit is readable when all of its consonant ids, its matra, its
 * independent vowel, its signs, and its numeral are taught; a multi-consonant
 * cluster additionally requires `sign-halant` (the joiner). Non-gradeable
 * units — spaces, danda, and non-Devanagari punctuation — are always readable.
 *
 * An `other` unit that carries any *teachable* Devanagari codepoint (e.g. a
 * candra sign like ॉ/ऑ/ऍ/ॅ, which the parser can't grade) is NOT readable:
 * the learner has been given a glyph the curriculum never teaches, so the
 * string must fail closed. Only whitespace, danda, and non-Devanagari
 * characters keep an `other` unit acceptable.
 */
export function isReadable(devanagari: string, taught: Set<string>): boolean {
  for (const akshara of parseAksharas(devanagari)) {
    if (akshara.other) {
      // A non-gradeable unit is fine only if it holds no teachable Devanagari
      // codepoint — otherwise it smuggles in an untaught (often unteachable)
      // glyph past the per-item checks below.
      for (const char of akshara.text) {
        const codePoint = char.codePointAt(0);
        if (codePoint !== undefined && isTeachableDevanagari(codePoint)) {
          return false;
        }
      }
      continue;
    }
    for (const consonantId of akshara.consonantIds) {
      if (!taught.has(consonantId)) {
        return false;
      }
    }
    if (akshara.matraId !== null && !taught.has(akshara.matraId)) {
      return false;
    }
    if (akshara.vowelId !== null && !taught.has(akshara.vowelId)) {
      return false;
    }
    for (const signId of akshara.signIds) {
      if (!taught.has(signId)) {
        return false;
      }
    }
    if (akshara.numeralId !== null && !taught.has(akshara.numeralId)) {
      return false;
    }
    // A joined cluster (>1 consonant) is only readable once the halant — the
    // mark that mutes the inherent vowel to join them — has been taught.
    if (akshara.consonantIds.length > 1 && !taught.has("sign-halant")) {
      return false;
    }
  }
  return true;
}
